import useOrderStatusChipConfig from '@/hooks/order/useOrderStatusChipConfig';
import { Chip } from '@montugroup/design-system';
import constants from '../../../constants/constants';

export type OrderStatus =
  | 'delivered'
  | 'collected'
  | 'shippedByPharmacy'
  | 'readyForCollection'
  | 'orderPlaced'
  | 'cancelled'
  | 'splitOrder'
  | 'refunded'
  | 'dispenseInterval'
  | 'readyForCollection'
  | 'collected';

type OrderStatusChipProps = {
  status: OrderStatus;
};

const defaultTexts: Record<OrderStatus, string> = {
  orderPlaced: 'Order placed',
  shippedByPharmacy: 'Shipped by pharmacy',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  splitOrder: 'Split order',
  refunded: 'Refunded',
  dispenseInterval: 'Dispense interval',
  readyForCollection: 'Ready for collection',
  collected: 'Collected',
};

export const getOrderStatusChipProps = ({
  status,
  delivered,
  shouldRenderSplitOrderDetails = false,
  isClickAndCollectOrder = false,
}: {
  status: number;
  delivered: boolean;
  shouldRenderSplitOrderDetails?: boolean;
  isClickAndCollectOrder?: boolean;
}): OrderStatusChipProps => {
  if (status === constants.orderStatus.REFUNDED) {
    return { status: 'refunded' };
  }

  if (shouldRenderSplitOrderDetails && status !== constants.orderStatus.CANCELLED && !delivered) {
    return { status: 'splitOrder' };
  }

  if (
    status === constants.orderStatus.AWAITING_PAYMENT ||
    status === constants.orderStatus.PAID_NOT_APPROVED ||
    status === constants.orderStatus.APPROVED_NOT_PAID ||
    status === constants.orderStatus.AWAITING_SHIPMENT ||
    status === constants.orderStatus.AWAITING_DISPENSE
  ) {
    return { status: 'orderPlaced' };
  }

  if (status === constants.orderStatus.PHARMACY_DISPENSED && !delivered) {
    if (isClickAndCollectOrder) {
      return { status: 'readyForCollection' };
    }
    return { status: 'shippedByPharmacy' };
  }

  if (delivered && status !== constants.orderStatus.CANCELLED) {
    if (isClickAndCollectOrder) {
      return { status: 'collected' };
    }
    return { status: 'delivered' };
  }

  if (status === constants.orderStatus.CANCELLED) {
    return { status: 'cancelled' };
  }

  if (status === constants.orderStatus.INTERVAL_TIME) {
    return { status: 'dispenseInterval' };
  }

  return { status: 'orderPlaced' };
};

export function OrderStatusChip(props: OrderStatusChipProps) {
  const { status } = props;
  const { config } = useOrderStatusChipConfig();

  const { icon: Icon, color, backgroundColor, hideBorder } = config[status];

  return (
    <Chip
      label={defaultTexts[status]}
      sx={{
        fontWeight: 600,
        paddingLeft: 1,
        paddingRight: 1,
        paddingTop: 2,
        paddingBottom: 2,
        color,
        backgroundColor,
        border: `1px solid ${hideBorder ? backgroundColor : color}`,
      }}
      variant="outlined"
      icon={<Icon color="inherit" sx={{ color }} />}
    />
  );
}

export default OrderStatusChip;
